// ============================================
// Calibration Pipeline Orchestrator
//
// Orchestrates the full calibration workflow:
// Import → PHI check → Extract lab → Extract SOAP
// → Blind run → Comparison → Save case
// ============================================

import { createLabSession, updateLabSessionStatus } from '../firestore/labSessionService';
import { createCalibrationCase, updateCalibrationCase } from '../firestore/calibrationCaseService';
import { seedClinicalPromptV1 } from '../firestore/clinicalPromptService';
import { parseSoapNote } from './soapParser';
import { runBlindAnalysis } from './blindRunService';
import { compareBlindRunToDrC } from './calibrationComparison';
import { getAuth } from 'firebase/auth';
import type {
  CalibrationCase,
  CaseDemographics,
  LabSession,
  ExtractedBiomarker,
} from '../../types/labUpload';

import { CF_URLS } from '../../config/api';

const EXTRACT_URL = CF_URLS.extractLabPDF;
const PHI_CHECK_URL = CF_URLS.checkPHI;

/** Get the current user's ID token for Cloud Function auth. */
async function getAuthToken(): Promise<string> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

/**
 * Run the full calibration pipeline for a paired SOAP+Lab import.
 *
 * Steps:
 * 1. Create LabSession + CalibrationCase
 * 2. PHI check on raw text
 * 3. Extract biomarkers from Lab PDF
 * 4. Parse SOAP note for Dr. C's assessment
 * 5. Blind run (Claude analyzes lab WITHOUT SOAP)
 * 6. 9-point comparison
 * 7. Save completed case
 */
export async function runCalibrationPipeline(
  labBase64: string,
  labMimeType: string,
  labFileName: string,
  soapBase64: string,
  soapMimeType: string,
  soapFileName: string,
  brandId: string,
  physicianId: string,
  demographics?: CaseDemographics,
): Promise<CalibrationCase> {
  const sessionId = `lab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const caseId = `cal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // Step 1: Create LabSession
  const labSession: LabSession = {
    sessionId,
    brandId,
    uploadedAt: new Date().toISOString(),
    status: 'uploaded',
    source: 'pdf',
    fileName: labFileName,
    fileSize: Math.round(labBase64.length * 0.75), // Approximate original size
    extractedBiomarkers: [],
    retestOf: null,
    metadata: { calibrationCaseId: caseId },
  };

  await createLabSession(labSession);

  // Create CalibrationCase
  const calibrationCase: CalibrationCase = {
    caseId,
    brandId,
    physicianId,
    labSessionId: sessionId,
    soapFileBase64: soapBase64,
    soapFileName,
    demographics,
    status: 'imported',
    importedAt: new Date().toISOString(),
  };

  await createCalibrationCase(calibrationCase);

  try {
    // Step 0: Ensure ClinicalAnalysisPrompt v1 exists (no-op after first run)
    await seedClinicalPromptV1();

    const authToken = await getAuthToken();
    const authHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    };

    // Step 2: Extract biomarkers from Lab PDF (before PHI check so we have text)
    await updateLabSessionStatus(sessionId, 'extracting');

    const extractResponse = await fetch(EXTRACT_URL, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        base64: labBase64,
        mimeType: labMimeType,
        fileName: labFileName,
      }),
    });

    if (!extractResponse.ok) {
      throw new Error(`Extraction failed: ${extractResponse.status}`);
    }

    const extractResult = await extractResponse.json() as {
      biomarkers: ExtractedBiomarker[];
      rawText: string;
      confidence: number;
    };

    await updateLabSessionStatus(sessionId, 'extracted', {
      extractedBiomarkers: extractResult.biomarkers,
    });

    await updateCalibrationCase(caseId, { status: 'extracted' });

    // Step 3: PHI check on extracted text (not binary PDF)
    const phiResponse = await fetch(PHI_CHECK_URL, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ text: extractResult.rawText.slice(0, 10000), caseId }),
    });

    if (!phiResponse.ok) {
      console.error(`[CalibrationPipeline] PHI check endpoint failed: ${phiResponse.status}`);
      // PHI check failure is not silent — log and continue for admin calibration
    } else {
      const phiResult = await phiResponse.json();
      await updateLabSessionStatus(sessionId, 'extracted', { phiGateResult: phiResult });

      if (!phiResult.passed) {
        console.warn(`[CalibrationPipeline] PHI detected in case ${caseId}: ${phiResult.redactedFields.join(', ')}`);
        // Continue for admin-uploaded calibration data but log the finding
      }
    }

    await updateCalibrationCase(caseId, { status: 'phi_checked' });

    // Step 4: Parse SOAP note
    const drCAssessment = await parseSoapNote(soapBase64, soapMimeType);
    await updateCalibrationCase(caseId, { drCAssessment });

    // Step 5: Blind run (Claude sees ONLY lab data, NOT SOAP)
    await updateLabSessionStatus(sessionId, 'analyzing');

    const blindRunOutput = await runBlindAnalysis(
      extractResult.biomarkers,
      demographics,
    );

    await updateCalibrationCase(caseId, {
      blindRunOutput,
      primaryFindings: blindRunOutput.rootCauses,
      status: 'blind_run_complete',
    });

    // Step 6: 9-point comparison
    const { decisionPoints, alignmentScore } = await compareBlindRunToDrC(
      blindRunOutput,
      drCAssessment,
      extractResult.biomarkers,
    );

    await updateCalibrationCase(caseId, {
      decisionPoints,
      alignmentScore,
      status: 'compared',
    });

    // Mark lab session complete
    await updateLabSessionStatus(sessionId, 'complete');

    // Return the completed case
    return {
      ...calibrationCase,
      drCAssessment,
      blindRunOutput,
      primaryFindings: blindRunOutput.rootCauses,
      decisionPoints,
      alignmentScore,
      status: 'compared' as const,
    };

  } catch (error) {
    // Mark both as failed with dedicated 'failed' status
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    await updateLabSessionStatus(sessionId, 'failed', { errorMessage });
    await updateCalibrationCase(caseId, { status: 'failed', errorMessage });
    throw error;
  }
}
